import { useEffect, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  INITIAL_INCIDENT_CHAT_STATE,
  incidentChatReducer,
  useIncidentChatActions,
  useIncidentRealtime,
} from '@ngsos/portal-core/chat';
import { activateChat as activateCallerChat } from '@ngsos/portal-core/commands';
import { useIncidentChat, useIncidentHubEvent, useIncidentLiveState } from '@ngsos/portal-core/portal';

import type { CallerConnectedStatus } from '@/signalr';
import { NgButton, PanelInfoWithIcon } from '@/ui';
import { ChatMessageList } from './ChatMessageList';
import { ChatMessageListLoadingPlaceholder } from './ChatMessageListLoadingPlaceholder';
import { MessageSendPanel } from './MessageSendPanel';

export type IncidentChatProps = {
  incidentId: string;
  callerConnectedStatus: CallerConnectedStatus;
  incidentHasPhoneNumber: boolean;
  incidentIsReadOnly: boolean;
  hasActiveAppleEedVideo: boolean;
  videoExpanded: boolean;
  onRequestVideoCollapse: () => void;
};

export function IncidentChat({
  incidentId,
  callerConnectedStatus,
  incidentHasPhoneNumber,
  incidentIsReadOnly,
  hasActiveAppleEedVideo,
  videoExpanded,
  onRequestVideoCollapse,
}: IncidentChatProps) {
  const { t } = useTranslation();
  const realtime = useIncidentRealtime();
  const { allowChatTranslations, callerMessagePreview, callerIsSendingImage } = useIncidentLiveState(incidentId);
  const { messages, isLoaded } = useIncidentChat(incidentId);
  const [state, dispatch] = useReducer(incidentChatReducer, INITIAL_INCIDENT_CHAT_STATE, (initial) => ({
    ...initial,
    callerMessagePreview,
    isCallerSendingImage: callerIsSendingImage,
  }));

  const allowActivatingChat = !incidentIsReadOnly && incidentHasPhoneNumber;

  const lastMessageId = messages?.at(-1)?.id ?? null;
  const lastSeenMessageId = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (lastSeenMessageId.current !== undefined && lastSeenMessageId.current !== lastMessageId && lastMessageId !== null) {
      onRequestVideoCollapse();
    }
    lastSeenMessageId.current = lastMessageId;
  }, [lastMessageId, onRequestVideoCollapse]);

  useIncidentHubEvent(incidentId, 'CallerChatTextMessagePreviewReceived', (messagePreview) => {
    dispatch({ type: 'caller-text-preview-received', messagePreview });
    onRequestVideoCollapse();
  });
  useIncidentHubEvent(incidentId, 'CallerChatImageMessagePreviewReceived', (isSendingImage) => {
    dispatch({ type: 'caller-image-preview-received', isSendingImage });
  });

  const { sendMessage, sendImageMessage, sendMessagePreview } = useIncidentChatActions(realtime, dispatch);

  const translateChatChanged = async (translateChat: boolean) => {
    await realtime?.setChatTranslationsAllowed(translateChat);
  };

  const activateChat = async () => {
    await activateCallerChat(incidentId);
  };

  const activateChatWithAppleEedConfirmation = async () => {
    const confirmed = window.confirm(t('Chat_ActivateChatAppleEedVideoWarning'));
    if (confirmed) {
      await activateChat();
    }
  };

  const sendPanel = (
    <MessageSendPanel
      onMessageSend={sendMessage}
      onMessagePreviewTriggered={sendMessagePreview}
      onMessageImageSend={sendImageMessage}
      callerChatConnectionStatus={callerConnectedStatus}
      isChatLoading={!isLoaded}
      incidentIsReadOnly={incidentIsReadOnly}
      translateChat={allowChatTranslations}
      onTranslateChatChanged={translateChatChanged}
      isSendingImage={state.isSendingImage}
    />
  );

  return (
    <div className="incident-chat">
      {!isLoaded ? (
        <ChatMessageListLoadingPlaceholder />
      ) : messages === null ? (
        hasActiveAppleEedVideo || videoExpanded ? (
          allowActivatingChat && (
            <NgButton
              icon="fal fa-message-plus"
              text={t('Chat_ActivateChat')}
              size="small"
              onClick={() => void activateChatWithAppleEedConfirmation()}
            />
          )
        ) : (
          <PanelInfoWithIcon icon="fal fa-message-slash" text={t('Chat_ChatIsInactive')}>
            {allowActivatingChat && (
              <NgButton
                icon="fal fa-message-plus"
                text={t('Chat_ActivateChat')}
                size="small"
                onClick={() => void activateChat()}
              />
            )}
          </PanelInfoWithIcon>
        )
      ) : videoExpanded ? (
        <>
          <div className="incident-chat__collapsed">
            <NgButton
              icon="fal fa-message"
              text={t('Chat_ShowChat')}
              size="small"
              onClick={onRequestVideoCollapse}
            />
          </div>
          <div className="incident-chat__full">
            <div className="incident-chat__top-part">
              <ChatMessageList
                messages={messages}
                callerMessagePreview={state.callerMessagePreview}
                isCallerSendingImage={state.isCallerSendingImage}
                translateChat={allowChatTranslations}
              />
            </div>
            <div className="incident-chat__bottom-part">{sendPanel}</div>
          </div>
        </>
      ) : (
        <>
          <div className="incident-chat__top-part">
            <ChatMessageList
              messages={messages}
              callerMessagePreview={state.callerMessagePreview}
              isCallerSendingImage={state.isCallerSendingImage}
              translateChat={allowChatTranslations}
            />
          </div>

          <div className="incident-chat__bottom-part">{sendPanel}</div>
        </>
      )}
    </div>
  );
}
